import { ErrorBoundary } from "@platform/design-system";

import {
  PolicyVariants,
  POLICY_VARIANT_AQUAFINA_COVERAGE_TYPE_MAPPING,
  VIEW_MODES,
} from "../../constants";
import {
  AccidentCoverage,
  BasicLifeCoverage,
  CriticalIllnessCoverage,
  DentalCoverage,
  HospitalIndemnityCoverage,
  LtdCoverage,
  MedicalCoverage,
  Policy,
  StdCoverage,
  VisionCoverage,
  VoluntaryLifeCoverage,
} from "../../constants/aquafina";
import Std from "../coverages/std";
import Ltd from "../coverages/ltd";
import Vision from "../coverages/vision";
import BasicLife from "../coverages/basic-life";
import Dental from "../coverages/dental";
import VoluntaryLife from "../coverages/voluntary-life";
import Medical from "../coverages/medical";
import { ListPlanNames } from "../../utils";
import Hospital from "../coverages/hospital";
import Accident from "../coverages/accident";
import CriticalIllness from "../coverages/critical-illness";
import NoInformation from "../no-information";
import { PlanErrorBoundaryFallback } from "../plan-error-boundary-fallback";

import WrapperDetails from "./wrapper";

type CoverageInformationProps = {
  policyDetailsInfo: Policy;
  variant: PolicyVariants;
};

const CoverageInformation = ({
  policyDetailsInfo,
  variant,
}: CoverageInformationProps) => {
  const coverageType = POLICY_VARIANT_AQUAFINA_COVERAGE_TYPE_MAPPING[variant];
  const {
    groupInformation,
    groupContacts,
    billing,
    benefitClasses,
    [coverageType]: coverage,
  } = policyDetailsInfo;

  const planNames =
    (coverage && ListPlanNames(coverage.plans, coverageType, variant)) || [];

  return (
    <WrapperDetails
      planNames={planNames}
      groupInformation={groupInformation}
      groupContacts={groupContacts}
      billing={billing}
      benefitClasses={benefitClasses}
    >
      <ErrorBoundary fallback={PlanErrorBoundaryFallback}>
        {coverage ? (
          <>
            {coverageType === "dental" && (
              <Dental
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as DentalCoverage}
                variant={variant}
                displayV4Fields={false}
              />
            )}
            {coverageType === "ltd" && (
              <Ltd
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as LtdCoverage}
                displayV4Fields={false}
              />
            )}
            {coverageType === "std" && (
              <Std
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as StdCoverage}
                displayV4Fields={false}
              />
            )}
            {coverageType === "vision" && (
              <Vision
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as VisionCoverage}
                displayV4Fields={false}
              />
            )}
            {coverageType === "basicLife" && (
              <BasicLife
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as BasicLifeCoverage}
                displayV4Fields={false}
              />
            )}
            {coverageType === "volLife" && (
              <VoluntaryLife
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as VoluntaryLifeCoverage}
                displayV4Fields={false}
              />
            )}
            {coverageType === "hospitalIndemnity" && (
              <Hospital
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as HospitalIndemnityCoverage}
                displayV4Fields={false}
              />
            )}
            {coverageType === "medical" && (
              <Medical coverage={coverage as MedicalCoverage} />
            )}
            {coverageType === "accident" && (
              <Accident
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as AccidentCoverage}
                displayV4Fields={false}
              />
            )}
            {coverageType === "criticalIllness" && (
              <CriticalIllness
                viewMode={VIEW_MODES.POLICY}
                coverage={coverage as CriticalIllnessCoverage}
                displayV4Fields={false}
              />
            )}
          </>
        ) : (
          <NoInformation />
        )}
      </ErrorBoundary>
    </WrapperDetails>
  );
};

export default CoverageInformation;
